import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDebounce } from './useDebounce'

interface ApiResponse<T = any> {
  data?: T
  token?: string
  refreshToken?: string
  user?: any
  message?: string
}

interface ApiError {
  message: string
  status: number
}

export function useApi() {
  const { t } = useI18n()
  const loading = ref(false)
  const error = ref<ApiError | null>(null)

  const getToken = (): string | null => {
    return localStorage.getItem('nm_token')
  }

  const refreshToken = async (): Promise<string | null> => {
    try {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: localStorage.getItem('nm_refresh_token') }),
      })
      if (!res.ok) return null
      const data: ApiResponse = await res.json()
      if (data.token) {
        localStorage.setItem('nm_token', data.token)
        return data.token
      }
      return null
    } catch {
      return null
    }
  }

  const request = async <T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> => {
    loading.value = true
    error.value = null

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    const token = getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    try {
      let res = await fetch(`/api/v1${url}`, { ...options, headers })

      if (res.status === 401) {
        const newToken = await refreshToken()
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`
          res = await fetch(`/api/v1${url}`, { ...options, headers })
        } else {
          localStorage.removeItem('nm_token')
          localStorage.removeItem('nm_refresh_token')
          error.value = { message: t('common.authExpired'), status: 401 }
          throw error.value
        }
      }

      const data: ApiResponse<T> = await res.json()

      if (!res.ok) {
        error.value = { message: data.message || t('common.requestFailed'), status: res.status }
        throw error.value
      }

      return data
    } catch (e: any) {
      if (!error.value) {
        error.value = { message: e.message || t('common.networkError'), status: 0 }
      }
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const get = <T = any>(url: string) => request<T>(url)

  const post = <T = any>(url: string, body: any) =>
    request<T>(url, { method: 'POST', body: JSON.stringify(body) })

  const put = <T = any>(url: string, body: any) =>
    request<T>(url, { method: 'PUT', body: JSON.stringify(body) })

  const del = <T = any>(url: string) => request<T>(url, { method: 'DELETE' })

  const { debounced: debouncedGet } = useDebounce(async (url: string, callback: (data: any) => void) => {
    try {
      const result = await get(url)
      callback(result)
    } catch {}
  }, 300)

  return { loading, error, get, post, put, del, debouncedGet }
}
